import nativeSpec from "./native-spec.json" with { type: "json" };
import { apiBaseUrl, validateApiKey } from "../auth/client";
import { getActiveProfile, getValidAccessToken } from "../auth/profiles";
import type { Profile } from "../auth/types";

const HTTP_METHODS = new Set([
	"get",
	"put",
	"post",
	"delete",
	"options",
	"head",
	"patch",
	"trace",
]);

export { nativeSpec };

/**
 * Returns a filtered copy of the spec containing only paths whose operations
 * carry the given tag. Passed to incur's `{ fetch, openapi }` command to
 * auto-generate typed subcommands for one command group at a time.
 */
/**
 * Parameter names that are redundant in CLI help: `authorization` is injected by
 * the fetch proxy as a Bearer token, and `api-version-date` is an internal
 * versioning header. Matched case-insensitively (the spec names it
 * `Api-Version-Date`) and through `$ref`s into `components.parameters`.
 */
const HIDDEN_PARAMS = new Set(["authorization", "api-version-date"]);

/**
 * Account-scoping params the fetch proxy auto-fills from the active profile.
 * The spec often marks these `required`, which would make incur demand them on
 * the command line before the proxy ever runs — so we demote them to optional
 * (still overridable by power users) and let the proxy inject the account.
 */
const AUTO_FILLED_PARAMS = new Set(["company_id", "account_id"]);

/** `$ref` strings (e.g. `#/components/parameters/ApiVersionDate`) that resolve to a hidden param. */
function hiddenParamRefs(spec: Record<string, unknown>): Set<string> {
	const refs = new Set<string>();
	const components = (spec.components as Record<string, unknown> | undefined)
		?.parameters;
	if (components && typeof components === "object") {
		for (const [key, def] of Object.entries(
			components as Record<string, unknown>,
		)) {
			const name = (def as Record<string, unknown> | null)?.name;
			if (typeof name === "string" && HIDDEN_PARAMS.has(name.toLowerCase())) {
				refs.add(`#/components/parameters/${key}`);
			}
		}
	}
	return refs;
}

function isHiddenParam(p: unknown, hiddenRefs: Set<string>): boolean {
	if (p == null || typeof p !== "object") return false;
	const rec = p as Record<string, unknown>;
	if (typeof rec.$ref === "string" && hiddenRefs.has(rec.$ref)) return true;
	return (
		typeof rec.name === "string" && HIDDEN_PARAMS.has(rec.name.toLowerCase())
	);
}

function transformParams(
	op: Record<string, unknown>,
	hiddenRefs: Set<string>,
): Record<string, unknown> {
	// `authorization` is auto-injected by the proxy, so drop the security
	// requirement that makes incur render an `--authorization` option.
	const { security: _security, ...rest } = op;
	const params = rest.parameters;
	if (!Array.isArray(params)) return rest;
	const relaxed = params
		.filter((p: unknown) => !isHiddenParam(p, hiddenRefs))
		.map((p: unknown) => {
			if (
				p != null &&
				typeof p === "object" &&
				AUTO_FILLED_PARAMS.has(
					String((p as Record<string, unknown>).name ?? ""),
				)
			) {
				return { ...(p as Record<string, unknown>), required: false };
			}
			return p;
		});
	return { ...rest, parameters: relaxed };
}

export function filterSpecByTag(
	spec: Record<string, unknown>,
	tag: string,
): Record<string, unknown> {
	const hiddenRefs = hiddenParamRefs(spec);
	const paths: Record<string, Record<string, unknown>> = {};
	for (const [path, methods] of Object.entries(
		spec.paths as Record<string, Record<string, unknown>>,
	)) {
		const filtered: Record<string, unknown> = {};
		const nonOpEntries: [string, unknown][] = [];
		for (const [method, op] of Object.entries(methods)) {
			if (!HTTP_METHODS.has(method)) {
				nonOpEntries.push([method, op]);
				continue;
			}
			const tags = (op as Record<string, unknown>).tags;
			if (Array.isArray(tags) && tags.includes(tag)) {
				filtered[method] = transformParams(
					op as Record<string, unknown>,
					hiddenRefs,
				);
			}
		}
		if (Object.keys(filtered).length > 0) {
			for (const [k, v] of nonOpEntries) filtered[k] = v;
			paths[path] = filtered;
		}
	}
	return { ...spec, paths };
}

/** Body/query field names that carry the active account. */
const COMPANY_FIELDS = ["company_id", "account_id"];

interface SpecOperation {
	parameters?: Array<{ name?: string; in?: string }>;
	requestBody?: {
		content?: Record<
			string,
			{ schema?: { properties?: Record<string, unknown> } }
		>;
	};
}

/** Finds the spec operation for a concrete request path + method (templated segments are wildcards). */
function findOperation(method: string, pathname: string): SpecOperation | null {
	const paths = (
		nativeSpec as { paths?: Record<string, Record<string, unknown>> }
	).paths;
	if (!paths) return null;
	const want = pathname.split("/").filter(Boolean);
	const m = method.toLowerCase();
	for (const [template, ops] of Object.entries(paths)) {
		const segs = template.split("/").filter(Boolean);
		if (segs.length !== want.length) continue;
		const matches = segs.every(
			(seg, i) => (seg.startsWith("{") && seg.endsWith("}")) || seg === want[i],
		);
		if (matches && ops[m]) return ops[m] as SpecOperation;
	}
	return null;
}

function declaresQueryParam(op: SpecOperation, name: string): boolean {
	return (op.parameters ?? []).some((p) => p.name === name && p.in === "query");
}

function declaresBodyProp(op: SpecOperation, name: string): boolean {
	const props =
		op.requestBody?.content?.["application/json"]?.schema?.properties;
	return !!props && Object.prototype.hasOwnProperty.call(props, name);
}

/**
 * The Whop API nests its error message under `{ error: { message } }`, but incur
 * only reads a top-level `message`, so every failure collapses to "HTTP 400".
 * Lift the real message to the top level so spec commands report what's wrong.
 */
function extractApiMessage(payload: unknown): string | undefined {
	if (payload == null || typeof payload !== "object") return undefined;
	const p = payload as Record<string, unknown>;
	const err = p.error;
	if (
		err &&
		typeof err === "object" &&
		typeof (err as Record<string, unknown>).message === "string"
	) {
		return (err as Record<string, string>).message;
	}
	for (const value of [p.message, p.detail, err]) {
		if (typeof value === "string") return value;
	}
	return undefined;
}

let envCompanyId: Promise<string | undefined> | null = null;

function normalizeApiKey(key: string): string {
	return key.replace(/^Bearer\s+/i, "").trim();
}

async function resolveCompanyId(
	profile: Profile | null,
	apiKey: string,
): Promise<string | undefined> {
	if (profile?.companyId) return profile.companyId;

	const envKey = process.env.WHOP_API_KEY
		? normalizeApiKey(process.env.WHOP_API_KEY)
		: undefined;
	if (!profile && envKey && apiKey === envKey) {
		// Memoize so concurrent requests in one command share a single validation,
		// but never cache a miss: a transient failure or not-yet-valid key must be
		// re-checked on the next request rather than locking the process to "no
		// account". A rejection is swallowed to undefined so it stays non-fatal —
		// commerce then surfaces a clear error instead of the proxy throwing.
		envCompanyId ??= validateApiKey(envKey, apiBaseUrl())
			.then((result) =>
				result.status === "valid" || result.status === "underscoped"
					? result.identity.id
					: undefined,
			)
			.catch(() => undefined);
		const resolved = await envCompanyId;
		if (resolved === undefined) envCompanyId = null;
		return resolved;
	}

	return undefined;
}

/**
 * Resolves the active account id for hand-written commands, using the same rules
 * as the fetch proxy: the active profile's account, or — when only WHOP_API_KEY
 * is set (agents / CI) — the account the env key belongs to. Lets commands that
 * build their own requests reach the account without a saved login profile.
 */
export async function resolveActiveCompanyId(): Promise<string | undefined> {
	const profile = getActiveProfile();
	const rawKey = profile
		? await getValidAccessToken(profile)
		: (process.env.WHOP_API_KEY ?? null);
	if (!rawKey) return undefined;
	return resolveCompanyId(profile, normalizeApiKey(rawKey));
}

/**
 * Returns a fetch handler that proxies requests to the Whop API. It:
 *  - injects the active profile's credential as a Bearer token,
 *  - auto-fills the active account (company_id/account_id) on any operation that
 *    declares it but didn't receive it — user-scoped OAuth tokens require this,
 *  - surfaces the API's real error message instead of a bare "HTTP <status>".
 *
 * Passed to incur's `{ fetch, openapi }` command to drive spec subcommands.
 */
export function createWhopFetch(): (request: Request) => Promise<Response> {
	return async (request: Request): Promise<Response> => {
		const profile = getActiveProfile();
		const rawKey = profile
			? await getValidAccessToken(profile)
			: (process.env.WHOP_API_KEY ?? null);

		if (!rawKey) {
			const message = profile
				? "Stored credential not found. Run `whop login` again."
				: "Not logged in. Run `whop login` first.";
			return Response.json({ message }, { status: 401 });
		}

		const token = normalizeApiKey(rawKey);

		const incomingUrl = new URL(request.url);
		const op = findOperation(request.method, incomingUrl.pathname);
		const companyId = await resolveCompanyId(profile, token);
		const method = request.method.toUpperCase();

		let body = request.body ? await request.text() : undefined;

		// The single place account scoping happens: auto-fill the active account on
		// any operation that declares it but didn't receive it. Never overwrites a
		// value the caller set.
		if (op && companyId) {
			if (method === "GET" || method === "DELETE" || method === "HEAD") {
				for (const name of COMPANY_FIELDS) {
					if (
						!incomingUrl.searchParams.has(name) &&
						declaresQueryParam(op, name)
					) {
						incomingUrl.searchParams.set(name, companyId);
					}
				}
			} else {
				const parsed = body ? safeParse(body) : {};
				if (parsed && typeof parsed === "object") {
					const rec = parsed as Record<string, unknown>;
					let changed = false;
					for (const name of COMPANY_FIELDS) {
						// The API infers company_id from a plan and rejects it when one is
						// given, so never inject company_id alongside a plan_id.
						if (name === "company_id" && rec.plan_id != null) continue;
						if (rec[name] == null && declaresBodyProp(op, name)) {
							rec[name] = companyId;
							changed = true;
						}
					}
					if (changed) body = JSON.stringify(parsed);
				}
			}
		}

		const base = apiBaseUrl().replace(/\/$/, "");
		const target = `${base}${incomingUrl.pathname}${incomingUrl.search}`;

		const headers = new Headers(request.headers);
		headers.set("Authorization", `Bearer ${token}`);
		headers.set("Content-Type", "application/json");

		const res = await fetch(target, { method: request.method, headers, body });
		if (res.ok) return res;

		// Lift the API's nested error message to the top level for incur.
		const text = await res.text();
		const payload = safeParse(text);
		let message = extractApiMessage(payload) ?? text ?? `HTTP ${res.status}`;
		// This permission has no OAuth scope, so re-login can't grant it — it needs
		// an API-key login for an account that holds the permission.
		if (
			res.status === 403 &&
			/not authorized for the .* scope/i.test(message)
		) {
			message +=
				" This resource requires an API-key login (`whop login --api-key`).";
		}
		return Response.json(
			{ message, error: payload ?? text },
			{ status: res.status },
		);
	};
}

function safeParse(text: string): unknown {
	try {
		return JSON.parse(text);
	} catch {
		return null;
	}
}
